import PropTypes from 'prop-types';
import React from 'react';

/**
 * Split `text` on every case-insensitive occurrence of `query`.
 *
 * The Knockout version built a RegExp from the user's input and rendered the
 * result through an `html:` binding, which is why it needed escapeRegex from
 * directory/widget/regex.js. Matching on indexOf avoids both the escaping and
 * the innerHTML: the parts come back as data and React renders them as text.
 */
export function highlightParts(text, query) {
    if (!query) {
        return [{match: false, text}];
    }

    const parts = [];
    const haystack = text.toLowerCase();
    const needle = query.toLowerCase();
    let cursor = 0;

    for (;;) {
        const at = haystack.indexOf(needle, cursor);
        if (at === -1) {
            break;
        }
        if (at > cursor) {
            parts.push({match: false, text: text.slice(cursor, at)});
        }
        parts.push({match: true, text: text.slice(at, at + needle.length)});
        cursor = at + needle.length;
    }

    if (cursor < text.length) {
        parts.push({match: false, text: text.slice(cursor)});
    }

    return parts;
}

/**
 * Build the profile link, carrying the fund and skin through so the page the
 * member lands on stays in the fund's context.
 *
 * The Knockout template concatenated `?health_fund=…` onto a url that may
 * already have carried `?source=…`, which produced two query strings. Going
 * through URL means the parameters merge properly however the url arrives.
 */
export function profileHref(url, {healthFundId, skin}) {
    // Relative urls need a base; it is discarded when we take the path back.
    const parsed = new URL(url, 'https://healthshare.invalid');
    if (healthFundId) {
        parsed.searchParams.set('health_fund', healthFundId);
    }
    if (skin) {
        parsed.searchParams.set('skin', skin);
    }
    return `${parsed.pathname}${parsed.search}`;
}

export default function NameSearchResult({
    healthFundId = null,
    isHighlighted = false,
    onSelect = null,
    query = '',
    result,
    skin = null,
    target = '_blank',
}) {
    return (
        <li
            className={
                isHighlighted
                    ? 'name-search-results selected'
                    : 'name-search-results'
            }
        >
            <div className="hs-result-block">
                <a
                    href={profileHref(result.url, {healthFundId, skin})}
                    onClick={onSelect}
                    target={target}
                >
                    <div className="avatar">
                        <img alt="" height="60" src={result.avatar} width="60" />
                    </div>
                    <div className="content">
                        <h3 className="title">
                            {highlightParts(result.displayName, query).map(
                                (part, index) =>
                                    part.match ? (
                                        <span className="highlight" key={index}>
                                            {part.text}
                                        </span>
                                    ) : (
                                        <React.Fragment key={index}>
                                            {part.text}
                                        </React.Fragment>
                                    ),
                            )}
                        </h3>
                        <p className="truncate">
                            {result.specialties.join(', ')}
                        </p>
                        <p className="truncate">{result.locality}</p>
                    </div>
                </a>
            </div>
        </li>
    );
}

NameSearchResult.propTypes = {
    healthFundId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isHighlighted: PropTypes.bool,
    onSelect: PropTypes.func,
    query: PropTypes.string,
    result: PropTypes.shape({
        avatar: PropTypes.string,
        displayName: PropTypes.string.isRequired,
        locality: PropTypes.string,
        specialties: PropTypes.array,
        url: PropTypes.string.isRequired,
    }).isRequired,
    skin: PropTypes.string,
    // '_top' for the funds that need results to replace their whole page,
    // '_blank' for everyone else.
    target: PropTypes.string,
};

